import React, { useRef, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { ChevronDown, ExternalLink } from "lucide-react";
import { useI18n } from "../i18n";

const CAT_STYLES = {
  earlyLife: "bg-jlo-bronze/15 text-jlo-bronze border-jlo-bronze/40",
  music: "bg-jlo-gold/15 text-jlo-gold border-jlo-gold/40",
  film: "bg-jlo-red/20 text-[#e0a0a4] border-jlo-red/50",
  dance: "bg-jlo-bronze/15 text-jlo-bronze border-jlo-bronze/40",
  fashion: "bg-jlo-gold/15 text-jlo-gold border-jlo-gold/40",
  business: "bg-jlo-gray/15 text-jlo-gray border-jlo-gray/40",
  legacy: "bg-jlo-red/20 text-[#e0a0a4] border-jlo-red/50",
};

function TimelineCard({ item, index }) {
  const { d } = useI18n();
  const [open, setOpen] = useState(false);
  const t = d.timeline[item.id] || {};
  const left = index % 2 === 0;

  return (
    <div className={`relative md:flex md:items-stretch ${left ? "md:flex-row" : "md:flex-row-reverse"}`}>
      {/* Dot */}
      <div className="absolute left-4 md:left-1/2 top-2 -translate-x-1/2 z-10">
        <span className="block h-3.5 w-3.5 rounded-full bg-jlo-gold ring-4 ring-jlo-bg shadow-[0_0_12px_rgba(215,181,109,0.6)]" />
      </div>

      <div className="md:w-1/2" />
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className={`md:w-1/2 pl-12 md:pl-0 ${left ? "md:pr-12" : "md:pl-12"}`}
      >
        <div
          data-testid={`timeline-card-${item.id}`}
          className="glass rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:border-jlo-gold/60"
        >
          <div className="flex items-center justify-between gap-3 mb-3">
            <span className="font-display text-2xl font-bold gold-text">{item.year}</span>
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${CAT_STYLES[item.category]}`}>
              {d.categories[item.category]}
            </span>
          </div>
          <h3 className="font-display text-xl font-semibold text-jlo-warm">{t.title}</h3>
          <p className="mt-2 text-sm text-jlo-warm/75 leading-relaxed">{t.summary}</p>

          {open && (
            <p className="mt-3 text-sm text-jlo-gray leading-relaxed border-t border-jlo-gold/15 pt-3">
              {t.details}
            </p>
          )}

          <div className="mt-4 flex items-center justify-between gap-3">
            <button
              data-testid={`timeline-toggle-${item.id}`}
              onClick={() => setOpen((v) => !v)}
              className="focus-gold inline-flex items-center gap-1 text-xs font-semibold text-jlo-gold hover:text-jlo-warm transition-colors"
              aria-expanded={open}
            >
              {open ? d.ui.readLess : d.ui.readMore}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {item.sourceUrl && (
              <a
                href={item.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-jlo-gray hover:text-jlo-gold transition-colors"
              >
                {d.ui.source}: {item.sourceLabel}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function Timeline({ items }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start center", "end center"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 80, damping: 24 });

  return (
    <div ref={ref} className="relative" data-testid="timeline">
      {/* Track */}
      <div className="absolute left-4 md:left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-jlo-gold/15" aria-hidden="true" />
      {/* Progress */}
      <motion.div
        style={{ scaleY }}
        className="absolute left-4 md:left-1/2 top-0 bottom-0 -translate-x-1/2 w-px origin-top gold-rule"
        aria-hidden="true"
      />
      <div className="space-y-10 md:space-y-16">
        {items.map((item, i) => (
          <TimelineCard key={item.id} item={item} index={i} />
        ))}
      </div>
    </div>
  );
}
